import { useEffect, useMemo, useState } from "react";
import { Drawer, Select } from "antd";
import { Link, Navigate, useParams } from "react-router-dom";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { careService } from "../../services/careService";
import AuthLoading from "../../components/common/AuthLoading";

const STATUS_OPTIONS = ["SUBMITTED", "APPROVED", "DECLINED", "PAID", "CANCELLED"];
const REVIEW_STATUS_OPTIONS = ["APPROVED", "DECLINED", "PAID", "CANCELLED"];
const ADMIN_REVIEW_STATUSES = ["APPROVED", "DECLINED", "PAID"];
const CARE_MANAGE_TIMESHEET_PRIVILEGE = "CARE_MANAGE_TIMESHEET";
const PAY_CODE_OPTIONS = ["REGULAR", "OVERTIME"];
const TIMESHEET_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const clean = (value) => String(value || "").trim();
const currentUserId = (user = {}) => user?.id || user?.userId || user?.uid || "";
const normalizeRoleToken = (role) =>
  String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
const privilegeNameFor = (privilege) =>
  typeof privilege === "string"
    ? privilege
    : privilege?.name || privilege?.scope || privilege?.code || privilege?.privilege?.name || "";
const normalizePrivilegeToken = (privilege) =>
  privilegeNameFor(privilege)
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
const workspaceMemberUserId = (membership = {}) =>
  membership?.user?.id ||
  membership?.userId ||
  membership?.memberId ||
  membership?.user?.userId ||
  membership?.member?.id ||
  membership?.member?.userId ||
  "";
const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
};
const localTimestamp = (date, time) => `${date || today()}T${time}`;
const defaultEntry = (date = today()) => ({
  startTimestamp: localTimestamp(date, "09:00"),
  endTimestamp: localTimestamp(date, "17:00"),
  shiftType: "REGULAR",
  notes: "",
});
const storageKey = (careProfileId, workspaceId) => `oliveeb-care-timesheets:${careProfileId || `workspace:${workspaceId || "current"}`}`;
const toMinutes = (value) => {
  const [hours, minutes] = String(value || "").split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
};
const toTimestampMillis = (value) => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
};
const intervalForEntry = (entry = {}) => {
  const start = entryStart(entry);
  const end = entryEnd(entry);
  const startMs = toTimestampMillis(start);
  const endMs = toTimestampMillis(end);
  if (startMs === null || endMs === null || endMs <= startMs) return null;
  return { start, end, startMs, endMs };
};
const intervalsOverlap = (left, right) => left.startMs < right.endMs && left.endMs > right.startMs;
const dateFromEntry = (entry = {}) => String(entryStart(entry) || "").slice(0, 10);
const dateFromEntries = (entries = [], fallback = today()) =>
  dateFromEntry(entries.find((entry) => entryStart(entry)) || {}) || fallback;
const hoursBetween = (start, end) => {
  const startMs = toTimestampMillis(start);
  const endMs = toTimestampMillis(end);
  if (startMs !== null && endMs !== null) {
    return Math.max(0, endMs - startMs) / 3600000;
  }
  const startMinutes = toMinutes(start);
  const endMinutes = toMinutes(end);
  if (startMinutes === null || endMinutes === null) return 0;
  const adjustedEnd = endMinutes < startMinutes ? endMinutes + 24 * 60 : endMinutes;
  return Math.max(0, adjustedEnd - startMinutes) / 60;
};
const totalHours = (entries = []) =>
  entries.reduce((sum, entry) => sum + hoursBetween(entryStart(entry), entryEnd(entry)), 0);
const formatHours = (value) => {
  const hours = Number(value || 0);
  return `${hours.toFixed(2)} hr${Math.abs(hours - 1) < 0.005 ? "" : "s"}`;
};
const titleCase = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
const statusLabel = (status) => (status === "CANCELLED" ? "Canceled" : titleCase(status));
const localWrite = (careProfileId, workspaceId, rows) => {
  localStorage.setItem(storageKey(careProfileId, workspaceId), JSON.stringify(rows));
};
const userFor = (profile = {}) => profile?.userProfile || profile?.user || {};
const profileFor = (profile = {}) => profile?.profile || profile?.profileData || {};
const nameFor = (profile = {}) => {
  const user = userFor(profile);
  const base = profileFor(profile);
  return clean(user.name) ||
    clean(base.fullName) ||
    [base.firstName, base.middleName, base.lastName].map(clean).filter(Boolean).join(" ") ||
    clean(profile.name) ||
    "Care staff";
};
const staffNameFor = (row = {}, fallback = "Care staff") =>
  clean(row.caregiverName) ||
  clean(row.staffName) ||
  clean(row.userProfile?.name) ||
  clean(row.caregiver?.name) ||
  clean(row.caregiverEmail) ||
  clean(row.caregiverUserId) ||
  fallback;
const profileIdFor = (profile = {}) => profile.id || profile.careProfileId || "";
const normalizeCareProfile = (result) => {
  if (Array.isArray(result)) return result[0] || null;
  if (Array.isArray(result?.content)) return result.content[0] || null;
  if (Array.isArray(result?.data)) return result.data[0] || null;
  return result || null;
};
const userIdFor = (profile = {}) => {
  const user = userFor(profile);
  return user.id || profile.userProfileId || profile.userId || "";
};
const profileLookupKeyFor = (row = {}) => row.careProfileId || row.profileId || row.caregiverUserId || "";
const staffIdsForRow = (row = {}) => [row.careProfileId, row.profileId, row.caregiverUserId].map((value) => String(value || "")).filter(Boolean);
const staffNameWithProfiles = (row = {}, profilesById = {}, fallback = "Care staff") => {
  const matched = profilesById[profileLookupKeyFor(row)] || profilesById[row.caregiverUserId] || null;
  return staffNameFor(row, matched ? nameFor(matched) : fallback);
};
const staffEmailWithProfiles = (row = {}, profilesById = {}) => {
  const matched = profilesById[profileLookupKeyFor(row)] || profilesById[row.caregiverUserId] || null;
  return staffEmailFor(row) || matched?.userProfile?.email || matched?.user?.email || matched?.profile?.email || matched?.profileData?.email || "";
};
const staffEmailFor = (row = {}) =>
  clean(row.caregiverEmail) ||
  clean(row.userProfile?.email) ||
  clean(row.caregiver?.email) ||
  "";
const rowEntries = (row = {}) => row.entries || row.timeEntries || [];
const entryStart = (entry = {}) => entry.startTimestamp || entry.startAt || entry.startDateTime || entry.startTime || "";
const entryEnd = (entry = {}) => entry.endTimestamp || entry.endAt || entry.endDateTime || entry.endTime || "";
const clockInTimestampFor = (record = {}) =>
  record.clockInTimestamp ||
  record.clockInTimeStamp ||
  record.clockInAt ||
  record.clockedInAt ||
  record.clockIn ||
  record.clockInDateTime ||
  record.clockInDatetime ||
  record.clockInDate ||
  record.clockInTime ||
  record.startTimestamp ||
  record.startedAt ||
  record.createdAt ||
  "";
const clockOutTimestampFor = (record = {}) =>
  record.clockOutTimestamp ||
  record.clockOutTimeStamp ||
  record.clockOutAt ||
  record.clockedOutAt ||
  record.clockOut ||
  record.clockOutDateTime ||
  record.clockOutDatetime ||
  record.clockOutDate ||
  record.clockOutTime ||
  record.endTimestamp ||
  record.endedAt ||
  "";
const toDateTimeInputValue = (value, fallbackDate = today(), fallbackTime = "09:00") => {
  if (!value) return localTimestamp(fallbackDate, fallbackTime);
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw)) return raw.slice(0, 16);
  if (/^\d{2}:\d{2}/.test(raw)) return localTimestamp(fallbackDate, raw.slice(0, 5));
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return localTimestamp(fallbackDate, fallbackTime);
  const pad = (part) => String(part).padStart(2, "0");
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
};
const formatTimestamp = (value) => {
  if (!value) return "N/A";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};
const payCodesFor = (row = {}) =>
  [...new Set(rowEntries(row).map((entry) => entry.payCode || entry.shiftType).filter(Boolean))];
const payCodeForEntry = (entry = {}, fallback = "REGULAR") => entry.payCode || entry.shiftType || fallback;
const payCodeClass = (value = "REGULAR") =>
  `is-${String(value || "REGULAR").toLowerCase().replace(/_/g, "-")}`;
const locationText = (location = {}) =>
  clean(location.address) ||
  [location.addressLine1, location.addressLine2, location.city, location.state, location.zipCode, location.country].map(clean).filter(Boolean).join(", ") ||
  clean(location.name) ||
  [location.latitude, location.longitude].map(clean).filter(Boolean).join(", ") ||
  "No location captured";
const recordStatusLabel = (status) => statusLabel(String(status || "RECORDED").toUpperCase());
const dateOnly = (value) => String(value || "").slice(0, 10);
const timestampMillisOrNull = (value) => {
  const millis = toTimestampMillis(value);
  return millis === null ? null : millis;
};
const endOfDayTimestamp = (date) => `${date || today()}T23:59:59`;
const startOfDayTimestamp = (date) => `${date || today()}T00:00:00`;
const excelCell = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
const filenameDate = () => new Date().toISOString().slice(0, 10);
const periodForTimesheet = (row = {}) => {
  const entries = rowEntries(row);
  const starts = entries.map(entryStart).filter(Boolean);
  const ends = entries.map(entryEnd).filter(Boolean);
  const fallbackDate = row.workDate || row.date || dateOnly(starts[0]) || today();
  const start = starts.length
    ? starts.reduce((earliest, value) => (String(value) < String(earliest) ? value : earliest), starts[0])
    : startOfDayTimestamp(fallbackDate);
  const end = ends.length
    ? ends.reduce((latest, value) => (String(value) > String(latest) ? value : latest), ends[0])
    : endOfDayTimestamp(fallbackDate);
  return {
    start,
    end,
    startDate: dateOnly(start) || fallbackDate,
    endDate: dateOnly(end) || fallbackDate,
  };
};
const attendanceOverlapsPeriod = (record = {}, period = {}) => {
  const clockInMs = timestampMillisOrNull(clockInTimestampFor(record));
  if (clockInMs === null) return false;
  const clockOutMs = timestampMillisOrNull(clockOutTimestampFor(record)) ?? clockInMs;
  const periodStartMs = timestampMillisOrNull(startOfDayTimestamp(period.startDate));
  const periodEndMs = timestampMillisOrNull(endOfDayTimestamp(period.endDate));
  if (periodStartMs === null || periodEndMs === null) return true;
  return clockInMs <= periodEndMs && clockOutMs >= periodStartMs;
};
const PayCodePills = ({ codes }) => (
  <span className="timesheet-pay-code-pills">
    {(codes?.length ? codes : ["REGULAR"]).map((code) => (
      <em className={`timesheet-pay-code ${payCodeClass(code)}`} key={code}>
        {titleCase(code)}
      </em>
    ))}
  </span>
);
const ConflictNotice = ({ conflicts }) => {
  if (!conflicts.length) return null;
  const messages = [...new Set(conflicts.map((conflict) => conflict.message))];
  return (
    <div className="timesheet-conflict-notice" role="alert">
      <strong><i className="ti ti-alert-triangle" /> Time conflict detected</strong>
      {messages.map((message) => (
        <span key={message}>{message}</span>
      ))}
    </div>
  );
};
const statusFor = (row = {}) => String(row.status || "SUBMITTED").toUpperCase();
const findTimesheetConflicts = ({ entries = [], existingRows = [], targetIds = [], excludeTimesheetId = "" }) => {
  const conflicts = [];
  const normalizedTargetIds = new Set(targetIds.map((value) => String(value || "")).filter(Boolean));
  const intervals = entries
    .map((entry, index) => ({ entry, index, interval: intervalForEntry(entry) }))
    .filter((item) => item.interval);

  intervals.forEach((left, leftIndex) => {
    intervals.slice(leftIndex + 1).forEach((right) => {
      if (!intervalsOverlap(left.interval, right.interval)) return;
      conflicts.push({
        index: left.index,
        message: `Entry ${left.index + 1} overlaps entry ${right.index + 1}.`,
      });
      conflicts.push({
        index: right.index,
        message: `Entry ${right.index + 1} overlaps entry ${left.index + 1}.`,
      });
    });
  });

  if (!normalizedTargetIds.size) return conflicts;

  existingRows
    .filter((row) => row.id !== excludeTimesheetId)
    .filter((row) => !["CANCELLED", "DECLINED"].includes(statusFor(row)))
    .filter((row) => staffIdsForRow(row).some((id) => normalizedTargetIds.has(id)))
    .forEach((row) => {
      rowEntries(row).forEach((existingEntry) => {
        const existingInterval = intervalForEntry(existingEntry);
        if (!existingInterval) return;
        intervals.forEach(({ index, interval }) => {
          if (!intervalsOverlap(interval, existingInterval)) return;
          conflicts.push({
            index,
            message: `Entry ${index + 1} overlaps existing time ${formatTimestamp(existingInterval.start)} - ${formatTimestamp(existingInterval.end)}.`,
          });
        });
      });
    });

  return conflicts;
};
const formFromTimesheet = (row = {}) => ({
  workDate: row.workDate || row.date || today(),
  status: statusFor(row),
  notes: row.notes || "",
  entries: rowEntries(row).length
    ? rowEntries(row).map((entry) => ({
        startTimestamp: toDateTimeInputValue(entryStart(entry), row.workDate || row.date || today(), "09:00"),
        endTimestamp: toDateTimeInputValue(entryEnd(entry), row.workDate || row.date || today(), "17:00"),
        shiftType: entry.shiftType || entry.payCode || "REGULAR",
        notes: entry.notes || row.notes || "",
      }))
    : [defaultEntry(row.workDate || row.date || today())],
});

export default function CareStaffTimesheetsPage({ scope = "workspace" }) {
  const { careProfileId } = useParams();
  const { user, careRoles = [], privileges = [], refreshCareRoles } = useAuth();
  const { selectedWorkspaceId, selectedWorkspace, workspaceListReady } = useWorkspace();
  const actorId = currentUserId(user);
  const [resolvedCareRoles, setResolvedCareRoles] = useState(() => careRoles);
  const [rolesRefreshing, setRolesRefreshing] = useState(Boolean(refreshCareRoles));
  const normalizedCareRoles = resolvedCareRoles.map(normalizeRoleToken);
  const currentWorkspaceMembership = (selectedWorkspace?.users || []).find(
    (membership) => String(workspaceMemberUserId(membership)) === String(actorId),
  );
  const normalizedPrivileges = [
    ...privileges,
    ...(currentWorkspaceMembership?.privilegeScopes || []),
    ...(currentWorkspaceMembership?.privileges || []),
    ...resolvedCareRoles,
  ].map(normalizePrivilegeToken);
  const hasManageTimesheetPrivilege = normalizedPrivileges.includes(CARE_MANAGE_TIMESHEET_PRIVILEGE);
  const canCreateForAnyStaff = normalizedCareRoles.includes("care_admin") || normalizedCareRoles.includes("care_owner");
  const canApproveTimesheets = canCreateForAnyStaff || hasManageTimesheetPrivilege;
  const visibleStatusOptions = canApproveTimesheets
    ? STATUS_OPTIONS
    : STATUS_OPTIONS.filter((status) => !ADMIN_REVIEW_STATUSES.includes(status));
  const editableStatusOptions = canApproveTimesheets
    ? STATUS_OPTIONS
    : visibleStatusOptions;
  const reviewStatusOptions = canApproveTimesheets
    ? REVIEW_STATUS_OPTIONS
    : REVIEW_STATUS_OPTIONS.filter((status) => !ADMIN_REVIEW_STATUSES.includes(status));
  const isMyTimesheet = scope === "mine";
  const isStaffTimesheet = Boolean(careProfileId);
  const isIndividualTimesheet = isStaffTimesheet || isMyTimesheet;
  const [profile, setProfile] = useState(null);
  const [careProfiles, setCareProfiles] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [rangeStart, setRangeStart] = useState(() => daysAgo(29));
  const [rangeEnd, setRangeEnd] = useState(() => today());
  const [staffFilter, setStaffFilter] = useState("ALL");
  const [payCodeFilter, setPayCodeFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [printedAtLabel, setPrintedAtLabel] = useState("");
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [editingTimesheet, setEditingTimesheet] = useState(null);
  const [reviewingTimesheet, setReviewingTimesheet] = useState(null);
  const [reviewForm, setReviewForm] = useState({ status: "APPROVED", reason: "" });
  const [reviewAttendanceOpen, setReviewAttendanceOpen] = useState(false);
  const [reviewAttendanceRecords, setReviewAttendanceRecords] = useState([]);
  const [reviewAttendanceLoading, setReviewAttendanceLoading] = useState(false);
  const [reviewAttendanceError, setReviewAttendanceError] = useState("");
  const [editForm, setEditForm] = useState(() => formFromTimesheet());
  const [newDrawerOpen, setNewDrawerOpen] = useState(false);
  const [selectedNewCareProfileId, setSelectedNewCareProfileId] = useState("");

  useEffect(() => {
    if (!refreshCareRoles) setResolvedCareRoles(careRoles);
  }, [careRoles, refreshCareRoles]);
  const [form, setForm] = useState({
    workDate: today(),
    notes: "",
    entries: [defaultEntry(today())],
  });

  useEffect(() => {
    let active = true;
    if (!workspaceListReady || !selectedWorkspaceId) {
      setLoading(false);
      return () => {
        active = false;
      };
    }
    setLoading(true);
    setError("");
    const workspaceParams = { actorId, wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId };
    const shouldLoadOwnProfile = isMyTimesheet || (!isStaffTimesheet && !canCreateForAnyStaff);
    const requests = shouldLoadOwnProfile
      ? [
          careService.careProfiles.getMine({
            actorId,
            userId: actorId,
            workspaceId: selectedWorkspaceId,
            wid: selectedWorkspaceId,
          }),
          Promise.resolve([]),
          Promise.resolve([]),
        ]
      : isStaffTimesheet
      ? [
          careService.careProfiles.get(careProfileId),
          careService.timesheets.listForCareProfile(careProfileId, workspaceParams),
          Promise.resolve([]),
        ]
      : [
          Promise.resolve(null),
          careService.timesheets.list({
            ...workspaceParams,
            ...(statusFilter !== "ALL" ? { status: statusFilter } : {}),
          }),
          careService.careProfiles.list(workspaceParams),
        ];
    Promise.allSettled(requests)
      .then(([profileResult, timesheetsResult, profilesResult]) => {
        if (!active) return;
        let resolvedProfile = null;
        if (profileResult.status === "fulfilled") {
          resolvedProfile = normalizeCareProfile(profileResult.value);
          setProfile(resolvedProfile);
          if ((isIndividualTimesheet || shouldLoadOwnProfile) && resolvedProfile) setCareProfiles([resolvedProfile]);
        }
        else if (isIndividualTimesheet) setError(profileResult.reason?.message || "Unable to load care profile.");
        if (!isIndividualTimesheet && profilesResult.status === "fulfilled") {
          setCareProfiles(Array.isArray(profilesResult.value) ? profilesResult.value : []);
        }
        const resolvedCareProfileId = isMyTimesheet || (!isStaffTimesheet && !canCreateForAnyStaff)
          ? profileIdFor(resolvedProfile || {})
          : careProfileId;
        const loadTimesheets = isMyTimesheet && resolvedCareProfileId
          ? careService.timesheets.listForCareProfile(resolvedCareProfileId, workspaceParams)
          : Promise.resolve(timesheetsResult.status === "fulfilled" ? timesheetsResult.value : null);

        return loadTimesheets.then((loadedRows) => {
          if (!active) return;
          if (loadedRows !== null) {
            const serverRows = Array.isArray(loadedRows) ? loadedRows : [];
            setTimesheets(serverRows);
            return;
          }
          throw timesheetsResult.reason || new Error("Unable to load timesheets.");
        }).catch((loadError) => {
          if (!active) return;
          setTimesheets([]);
          setError(loadError?.response?.data?.message || loadError?.message || "Unable to load timesheets.");
        });
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [actorId, canCreateForAnyStaff, careProfileId, isIndividualTimesheet, isMyTimesheet, isStaffTimesheet, selectedWorkspaceId, statusFilter, workspaceListReady]);

  useEffect(() => {
    let active = true;
    if (!refreshCareRoles) {
      setRolesRefreshing(false);
      return () => {
        active = false;
      };
    }
    if (!workspaceListReady || !actorId) {
      setRolesRefreshing(true);
      return () => {
        active = false;
      };
    }
    if (!selectedWorkspaceId) {
      setRolesRefreshing(false);
      return () => {
        active = false;
      };
    }

    setRolesRefreshing(true);
    refreshCareRoles({ workspaceId: selectedWorkspaceId, wid: selectedWorkspaceId })
      .then((nextRoles) => {
        if (active) setResolvedCareRoles(Array.isArray(nextRoles) ? nextRoles : []);
      })
      .finally(() => {
        if (active) setRolesRefreshing(false);
      });

    return () => {
      active = false;
    };
  }, [actorId, refreshCareRoles, selectedWorkspaceId, workspaceListReady]);

  useEffect(() => {
    if (!canApproveTimesheets && ADMIN_REVIEW_STATUSES.includes(statusFilter)) {
      setStatusFilter("ALL");
    }
    if (!canApproveTimesheets && ADMIN_REVIEW_STATUSES.includes(editForm.status)) {
      setEditForm((current) => ({ ...current, status: "SUBMITTED" }));
    }
  }, [canApproveTimesheets, editForm.status, statusFilter]);

  const staffName = isIndividualTimesheet ? nameFor(profile || {}) : "Workspace";
  const resolvedCareProfileId = isMyTimesheet ? profileIdFor(profile || {}) : careProfileId;
  const currentStaffUserId = userIdFor(profile || {});
  const canCreateOnStaffPage = !isStaffTimesheet || canCreateForAnyStaff || (currentStaffUserId && currentStaffUserId === actorId);
  const chartPath = isMyTimesheet ? "/my-timesheet/chart" : "/timesheets/chart";
  const pageTitle = isMyTimesheet ? "My Timesheet" : isStaffTimesheet ? `${staffName} Timesheets` : "Timesheets";
  const pageSubtitle = isMyTimesheet
    ? "Review your submitted hours and add your own worked time."
    : isStaffTimesheet
      ? "Review previous timesheets and add worked hours for this care staff member."
      : "Review staff timesheets captured in this workspace.";

  const profilesById = useMemo(() => {
    const lookup = {};
    careProfiles.forEach((careProfile) => {
      const profileId = profileIdFor(careProfile);
      const userId = userIdFor(careProfile);
      if (profileId) lookup[profileId] = careProfile;
      if (userId) lookup[userId] = careProfile;
    });
    return lookup;
  }, [careProfiles]);
  const staffChoices = useMemo(
    () => careProfiles
      .map((careProfile) => ({
        id: profileIdFor(careProfile),
        userId: userIdFor(careProfile),
        name: nameFor(careProfile),
        email: careProfile?.userProfile?.email || careProfile?.user?.email || careProfile?.profile?.email || careProfile?.profileData?.email || "",
      }))
      .filter((item) => item.id)
      .sort((left, right) => left.name.localeCompare(right.name)),
    [careProfiles],
  );
  const selectedNewStaff = useMemo(
    () => staffChoices.find((item) => item.id === selectedNewCareProfileId) || null,
    [selectedNewCareProfileId, staffChoices],
  );
  const ownCareProfileId = profileIdFor(profile || {});
  const activeFormCareProfileId = resolvedCareProfileId || (canCreateForAnyStaff ? selectedNewCareProfileId : ownCareProfileId);
  const activeFormStaff = useMemo(
    () => staffChoices.find((item) => item.id === activeFormCareProfileId) || selectedNewStaff,
    [activeFormCareProfileId, selectedNewStaff, staffChoices],
  );
  const formConflicts = useMemo(
    () => findTimesheetConflicts({
      entries: form.entries,
      existingRows: timesheets,
      targetIds: [activeFormCareProfileId, activeFormStaff?.userId],
    }),
    [activeFormCareProfileId, activeFormStaff?.userId, form.entries, timesheets],
  );
  const formConflictIndexes = useMemo(
    () => new Set(formConflicts.map((conflict) => conflict.index)),
    [formConflicts],
  );
  const editConflicts = useMemo(
    () => findTimesheetConflicts({
      entries: editForm.entries,
      existingRows: timesheets,
      targetIds: staffIdsForRow(editingTimesheet || {}),
      excludeTimesheetId: editingTimesheet?.id || "",
    }),
    [editForm.entries, editingTimesheet, timesheets],
  );
  const editConflictIndexes = useMemo(
    () => new Set(editConflicts.map((conflict) => conflict.index)),
    [editConflicts],
  );
  const formHours = useMemo(() => totalHours(form.entries), [form.entries]);
  const rangeTimesheetEntries = useMemo(() => {
    return timesheets
      .flatMap((row) => {
        const entries = rowEntries(row);
        const displayEntries = entries.length ? entries : [{
          startTimestamp: row.startTimestamp || row.startAt || row.startDateTime || row.startTime || row.workDate || row.date,
          endTimestamp: row.endTimestamp || row.endAt || row.endDateTime || row.endTime || row.workDate || row.date,
          payCode: row.payCode || row.shiftType || "REGULAR",
          hours: row.totalHours,
        }];
        return displayEntries.map((entry, entryIndex) => ({
          row,
          entry,
          entryIndex,
          start: entryStart(entry),
          end: entryEnd(entry),
          payCode: payCodeForEntry(entry, row.payCode || row.shiftType || "REGULAR"),
        }));
      })
      .filter(({ row, start, payCode }) => {
        const date = String(start || row.workDate || row.date || "").slice(0, 10);
        if (rangeStart && date < rangeStart) return false;
        if (rangeEnd && date > rangeEnd) return false;
        if (staffFilter !== "ALL") {
          const selectedStaff = staffChoices.find((staff) => staff.id === staffFilter);
          const rowStaffIds = [row.careProfileId, row.profileId, row.caregiverUserId].map((value) => String(value || ""));
          if (!rowStaffIds.includes(staffFilter) && (!selectedStaff?.userId || !rowStaffIds.includes(String(selectedStaff.userId)))) return false;
        }
        if (payCodeFilter !== "ALL" && payCode !== payCodeFilter) return false;
        return true;
      })
      .sort((left, right) => String(right.start || right.row.workDate || right.row.date).localeCompare(String(left.start || left.row.workDate || left.row.date)));
  }, [payCodeFilter, rangeEnd, rangeStart, staffChoices, staffFilter, timesheets]);
  const filteredTimesheetEntries = useMemo(
    () => rangeTimesheetEntries.filter(({ row }) => statusFilter === "ALL" || statusFor(row) === statusFilter),
    [rangeTimesheetEntries, statusFilter],
  );
  const totalPages = Math.max(1, Math.ceil(filteredTimesheetEntries.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedTimesheetEntries = useMemo(
    () => {
      const startIndex = (currentPage - 1) * pageSize;
      return filteredTimesheetEntries.slice(startIndex, startIndex + pageSize);
    },
    [currentPage, filteredTimesheetEntries, pageSize],
  );
  const paginationStart = filteredTimesheetEntries.length ? ((currentPage - 1) * pageSize) + 1 : 0;
  const paginationEnd = Math.min(filteredTimesheetEntries.length, currentPage * pageSize);
  const rangeTotal = useMemo(
    () => filteredTimesheetEntries.reduce((sum, { entry, start, end }) => sum + Number(entry.hours ?? hoursBetween(start, end)), 0),
    [filteredTimesheetEntries],
  );
  const statusSummary = useMemo(() => {
    const lookup = {};
    rangeTimesheetEntries.forEach(({ row, entry, start, end }) => {
      const status = statusFor(row);
      const current = lookup[status] || { status, hours: 0, count: 0 };
      current.hours += Number(entry.hours ?? hoursBetween(start, end));
      current.count += 1;
      lookup[status] = current;
    });
    return STATUS_OPTIONS
      .map((status) => lookup[status] || { status, hours: 0, count: 0 });
  }, [rangeTimesheetEntries]);
  const reviewingTimesheetPeriod = useMemo(
    () => (reviewingTimesheet ? periodForTimesheet(reviewingTimesheet) : null),
    [reviewingTimesheet],
  );

  useEffect(() => {
    setPage(1);
  }, [rangeStart, rangeEnd, staffFilter, payCodeFilter, statusFilter, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const clearPrintMode = () => document.body.classList.remove("printing-timesheets");
    window.addEventListener("afterprint", clearPrintMode);
    return () => {
      window.removeEventListener("afterprint", clearPrintMode);
      clearPrintMode();
    };
  }, []);

  const updateEntry = (index, key, value) =>
    setForm((current) => ({
      ...current,
      entries: current.entries.map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, [key]: value } : entry,
      ),
    }));
  const addEntry = () =>
    setForm((current) => ({
      ...current,
      entries: [defaultEntry(dateFromEntries(current.entries)), ...current.entries],
    }));
  const removeEntry = (index) =>
    setForm((current) => ({
      ...current,
      entries: current.entries.filter((_entry, entryIndex) => entryIndex !== index),
    }));
  const updateEditEntry = (index, key, value) =>
    setEditForm((current) => ({
      ...current,
      entries: current.entries.map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, [key]: value } : entry,
      ),
    }));
  const addEditEntry = () =>
    setEditForm((current) => ({
      ...current,
      entries: [defaultEntry(dateFromEntries(current.entries)), ...current.entries],
    }));
  const removeEditEntry = (index) =>
    setEditForm((current) => ({
      ...current,
      entries: current.entries.filter((_entry, entryIndex) => entryIndex !== index),
    }));
  const openEditTimesheet = (row) => {
    setEditingTimesheet(row);
    setEditForm(formFromTimesheet(row));
  };
  const openReviewTimesheet = (row, status = "APPROVED") => {
    if (ADMIN_REVIEW_STATUSES.includes(status) && isMyTimesheet && isOwnTimesheetRow(row)) {
      setError("You cannot approve, decline, or pay your own timesheet.");
      return;
    }
    if (ADMIN_REVIEW_STATUSES.includes(status) && !canApproveTimesheets) {
      setError("Only care admins and care owners can approve, decline, or pay timesheets.");
      return;
    }
    setReviewingTimesheet(row);
    setReviewAttendanceOpen(false);
    setReviewAttendanceRecords([]);
    setReviewAttendanceError("");
    setReviewForm({
      status,
      reason: row.statusReason || row.reason || "",
    });
  };
  const closeReviewTimesheet = () => {
    setReviewingTimesheet(null);
    setReviewAttendanceOpen(false);
    setReviewAttendanceRecords([]);
    setReviewAttendanceError("");
  };
  const replaceTimesheet = (nextRow) => {
    setTimesheets((current) => current.map((row) => (row.id === nextRow.id ? nextRow : row)));
  };
  const removeTimesheetFromView = (timesheetId) => {
    setTimesheets((current) => current.filter((row) => row.id !== timesheetId));
  };
  const isOwnTimesheetRow = (row = {}) => {
    const rowIds = staffIdsForRow(row);
    return [actorId, resolvedCareProfileId, currentStaffUserId]
      .map((value) => String(value || ""))
      .filter(Boolean)
      .some((id) => rowIds.includes(id));
  };
  const canMutateTimesheetRow = (row = {}) =>
    canCreateForAnyStaff || isOwnTimesheetRow(row);
  const canReviewTimesheetRow = (row = {}) =>
    canApproveTimesheets && !(isMyTimesheet && isOwnTimesheetRow(row));
  const exportTimesheetRecordsToExcel = () => {
    if (typeof window === "undefined" || !filteredTimesheetEntries.length) return;

    const rows = filteredTimesheetEntries.map(({ row, entry, start, end, payCode }) => [
      staffNameWithProfiles(row, profilesById, "Care staff"),
      staffEmailWithProfiles(row, profilesById) || row.caregiverUserId || "",
      formatTimestamp(start),
      formatTimestamp(end),
      formatHours(entry.hours ?? hoursBetween(start, end)),
      titleCase(payCode),
      statusLabel(statusFor(row)),
      entry.notes || row.notes || "",
    ]);
    const html = `<!doctype html>
<html>
<head><meta charset="utf-8" /></head>
<body>
  <table>
    <thead>
      <tr><th>Staff</th><th>Email</th><th>Start</th><th>End</th><th>Hours</th><th>Pay code</th><th>Status</th><th>Notes</th></tr>
    </thead>
    <tbody>
      ${rows.map((cells) => `<tr>${cells.map((cell) => `<td>${excelCell(cell)}</td>`).join("")}</tr>`).join("")}
    </tbody>
  </table>
</body>
</html>`;
    const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `timesheets-${filenameDate()}.xls`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };
  const printTimesheetRecords = () => {
    if (typeof window === "undefined") return;
    setPrintedAtLabel(new Date().toLocaleString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }));
    document.body.classList.add("printing-timesheets");
    window.setTimeout(() => window.print(), 50);
  };

  const saveTimesheet = async () => {
    const targetCareProfileId = resolvedCareProfileId || (canCreateForAnyStaff ? selectedNewCareProfileId : ownCareProfileId);
    if (!targetCareProfileId) {
      setError(canCreateForAnyStaff ? "Select a staff member." : "Your care staff profile is required before creating a timesheet.");
      return;
    }
    if (!canCreateForAnyStaff && isStaffTimesheet && currentStaffUserId !== actorId) {
      setError("You can only create your own timesheet.");
      return;
    }
    if (!canCreateForAnyStaff && !isStaffTimesheet && ownCareProfileId && targetCareProfileId !== ownCareProfileId) {
      setError("You can only create your own timesheet.");
      return;
    }
    const validEntries = form.entries.filter((entry) => entry.startTimestamp && entry.endTimestamp);
    if (!validEntries.length) {
      setError("Add at least one start and end timestamp.");
      return;
    }
    if (formConflicts.length) {
      setError("Resolve overlapping time entries before saving.");
      return;
    }
    setSaving(true);
    setError("");
    const targetStaff = canCreateForAnyStaff ? selectedNewStaff : activeFormStaff;
    const staffMeta = {
      caregiverName: targetStaff?.name || (isIndividualTimesheet ? staffName : ""),
      caregiverEmail: targetStaff?.email || staffChoices[0]?.email || "",
      caregiverUserId: targetStaff?.userId || staffChoices[0]?.userId || "",
    };
    const payloads = validEntries.map((entry) => ({
      actorId,
      careProfileId: targetCareProfileId,
      workspaceId: selectedWorkspaceId,
      workDate: dateFromEntries([entry]),
      entries: [entry],
      totalHours: Number(hoursBetween(entry.startTimestamp, entry.endTimestamp).toFixed(2)),
      status: "SUBMITTED",
      notes: entry.notes || "",
    }));
    try {
      const results = await Promise.allSettled(
        payloads.map((payload) => careService.timesheets.createForCareProfile(
          targetCareProfileId,
          payload,
          { wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId },
        )),
      );
      const failed = results.find((result) => result.status === "rejected");
      if (failed) {
        throw failed.reason;
      }
      const nextRows = results.map((result, index) => result.value || {
        ...payloads[index],
        ...staffMeta,
      });
      const next = [...nextRows, ...timesheets];
      setTimesheets(next);
      localWrite(targetCareProfileId, selectedWorkspaceId, next);
      localWrite(null, selectedWorkspaceId, next);
      setNewDrawerOpen(false);
      setSelectedNewCareProfileId("");
      setForm({
        workDate: today(),
        notes: "",
        entries: [defaultEntry(today())],
      });
    } catch (saveError) {
      setError(saveError?.response?.data?.message || saveError?.message || "Unable to save timesheet.");
    } finally {
      setSaving(false);
    }
  };
  const saveEditedTimesheet = async () => {
    if (!editingTimesheet?.id) return;
    const validEntries = editForm.entries.filter((entry) => entry.startTimestamp && entry.endTimestamp);
    if (!validEntries.length) {
      setError("Add at least one start and end timestamp.");
      return;
    }
    if (editConflicts.length) {
      setError("Resolve overlapping time entries before saving.");
      return;
    }
    setSaving(true);
    setError("");
    const targetCareProfileId = editingTimesheet.careProfileId || careProfileId;
    const firstEntry = validEntries[0];
    const payload = {
      careProfileId: targetCareProfileId,
      workspaceId: selectedWorkspaceId,
      workDate: dateFromEntries([firstEntry], editingTimesheet.workDate || editingTimesheet.date || today()),
      entries: [firstEntry],
      totalHours: Number(hoursBetween(firstEntry.startTimestamp, firstEntry.endTimestamp).toFixed(2)),
      status: editForm.status || statusFor(editingTimesheet),
      notes: firstEntry.notes || "",
    };
    try {
      const saved = await careService.timesheets.update(
        editingTimesheet.id,
        payload,
        { actorId, wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId },
      );
      const extraPayloads = validEntries.slice(1).map((entry) => ({
        actorId,
        careProfileId: targetCareProfileId,
        workspaceId: selectedWorkspaceId,
        workDate: dateFromEntries([entry], editingTimesheet.workDate || editingTimesheet.date || today()),
        entries: [entry],
        totalHours: Number(hoursBetween(entry.startTimestamp, entry.endTimestamp).toFixed(2)),
        status: editForm.status || statusFor(editingTimesheet),
        notes: entry.notes || "",
      }));
      const extraResults = await Promise.allSettled(
        extraPayloads.map((extraPayload) => careService.timesheets.createForCareProfile(
          targetCareProfileId,
          extraPayload,
          { wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId },
        )),
      );
      const failedExtra = extraResults.find((result) => result.status === "rejected");
      if (failedExtra) {
        throw failedExtra.reason;
      }
      const extraRows = extraResults.map((result, index) => result.value || {
        ...extraPayloads[index],
        caregiverName: staffNameWithProfiles(editingTimesheet, profilesById, staffName),
        caregiverEmail: staffEmailWithProfiles(editingTimesheet, profilesById),
        caregiverUserId: editingTimesheet.caregiverUserId,
      });
      const savedMain = saved || { ...editingTimesheet, ...payload };
      setTimesheets((current) => [savedMain, ...extraRows, ...current.filter((row) => row.id !== editingTimesheet.id)]);
      setEditingTimesheet(null);
    } catch (saveError) {
      setError(saveError?.response?.data?.message || saveError?.message || "Unable to save timesheet.");
    } finally {
      setSaving(false);
    }
  };
  const deleteTimesheet = async (row) => {
    if (!row?.id) return;
    openReviewTimesheet(row, "CANCELLED");
  };
  const loadReviewClockInOutDetails = async () => {
    if (!reviewingTimesheet) return;
    const period = reviewingTimesheetPeriod || periodForTimesheet(reviewingTimesheet);
    const rowStaffIds = staffIdsForRow(reviewingTimesheet);
    const matchedProfile = rowStaffIds
      .map((id) => profilesById[id])
      .find(Boolean);
    const targetCareProfileId = reviewingTimesheet.careProfileId || reviewingTimesheet.profileId || profileIdFor(matchedProfile || {});

    if (!targetCareProfileId) {
      setReviewAttendanceOpen(true);
      setReviewAttendanceRecords([]);
      setReviewAttendanceError("No care profile id is available for this timesheet.");
      return;
    }

    setReviewAttendanceOpen(true);
    setReviewAttendanceLoading(true);
    setReviewAttendanceError("");
    try {
      const workspaceParams = {
        actorId,
        wid: selectedWorkspaceId,
        workspaceId: selectedWorkspaceId,
        startDate: period.startDate,
        endDate: period.endDate,
      };
      const rows = await careService.attendance.listForCareProfile(targetCareProfileId, workspaceParams);
      const matchingRows = (Array.isArray(rows) ? rows : [])
        .filter((record) => attendanceOverlapsPeriod(record, period))
        .sort((left, right) => String(clockInTimestampFor(left) || "").localeCompare(String(clockInTimestampFor(right) || "")));
      setReviewAttendanceRecords(matchingRows);
    } catch (loadError) {
      setReviewAttendanceRecords([]);
      setReviewAttendanceError(loadError?.response?.data?.message || loadError?.message || "Unable to load ClockInOut details.");
    } finally {
      setReviewAttendanceLoading(false);
    }
  };
  const submitReview = async () => {
    if (!reviewingTimesheet?.id) return;
    if (!reviewForm.status) {
      setError("Select a review status.");
      return;
    }
    if (["DECLINED", "CANCELLED"].includes(reviewForm.status) && !clean(reviewForm.reason)) {
      setError("Enter a reason.");
      return;
    }
    if (ADMIN_REVIEW_STATUSES.includes(reviewForm.status) && !canApproveTimesheets) {
      setError("Only care admins and care owners can approve, decline, or pay timesheets.");
      return;
    }
    if (ADMIN_REVIEW_STATUSES.includes(reviewForm.status) && isMyTimesheet && isOwnTimesheetRow(reviewingTimesheet)) {
      setError("You cannot approve, decline, or pay your own timesheet.");
      return;
    }
    setSaving(true);
    setError("");
    const payload = {
      workspaceId: selectedWorkspaceId,
      status: reviewForm.status,
      statusReason: reviewForm.reason,
      reason: reviewForm.reason,
      notes: reviewingTimesheet.notes,
    };
    try {
      const reviewed = await careService.timesheets.update(reviewingTimesheet.id, payload, {
        actorId,
        wid: selectedWorkspaceId,
        workspaceId: selectedWorkspaceId,
      });
      const nextRow = reviewed || { ...reviewingTimesheet, ...payload };
      if (statusFilter !== "ALL" && statusFilter !== nextRow.status) removeTimesheetFromView(reviewingTimesheet.id);
      else replaceTimesheet(nextRow);
      closeReviewTimesheet();
    } catch (saveError) {
      setError(saveError?.response?.data?.message || saveError?.message || "Unable to save timesheet review.");
    } finally {
      setSaving(false);
    }
  };

  if (!isMyTimesheet && !isStaffTimesheet && rolesRefreshing) {
    return <AuthLoading />;
  }

  if (!isMyTimesheet && !isStaffTimesheet && !canCreateForAnyStaff) {
    return <Navigate to="/my-timesheet" replace />;
  }

  const reviewingOwnTimesheet = reviewingTimesheet ? isMyTimesheet && isOwnTimesheetRow(reviewingTimesheet) : false;
  const currentReviewStatusOptions = reviewingOwnTimesheet
    ? REVIEW_STATUS_OPTIONS.filter((status) => !ADMIN_REVIEW_STATUSES.includes(status))
    : reviewStatusOptions;
  const canEditTimesheetManagerOptions = editingTimesheet
    ? canApproveTimesheets && !(isMyTimesheet && isOwnTimesheetRow(editingTimesheet))
    : false;

  return (
    <CareShell className="dashboard-content residents-page care-staff-timesheets-page">
      <div className="residents-page-head">
        <div className="page-title">
          <h1>{pageTitle}</h1>
          <p>{pageSubtitle}</p>
        </div>
        <div className="care-staff-page-actions">
          {isStaffTimesheet ? (
            <Link className="mini-btn" to={`/staff/${careProfileId}`}>
              <i className="ti ti-arrow-left" /> Back to staff
            </Link>
          ) : (
            <button className="btn primary" type="button" onClick={() => setNewDrawerOpen(true)}>
              <i className="ti ti-plus" /> {isMyTimesheet || !canCreateForAnyStaff ? "Add Timesheet" : "New Timesheet"}
            </button>
          )}
          <Link className="mini-btn timesheet-nav-blue" to={chartPath}>
            <i className="ti ti-chart-line" /> Hours chart
          </Link>
          <button className="mini-btn timesheet-export-excel-button" type="button" onClick={exportTimesheetRecordsToExcel} disabled={!filteredTimesheetEntries.length}>
            <i className="ti ti-file-spreadsheet" /> Export Excel
          </button>
          <button className="mini-btn" type="button" onClick={printTimesheetRecords} disabled={!filteredTimesheetEntries.length}>
            <i className="ti ti-printer" /> Print records
          </button>
        </div>
      </div>

      {loading ? <Panel title="Timesheets"><p>Loading timesheets...</p></Panel> : null}
      {error ? <Panel title="Timesheets"><p className="form-error">{error}</p></Panel> : null}

      <section className="timesheet-summary-grid">
        <article>
          <span>Total in range</span>
          <strong>{formatHours(rangeTotal)}</strong>
          <small>{filteredTimesheetEntries.length} entr{filteredTimesheetEntries.length === 1 ? "y" : "ies"}</small>
          <div className="timesheet-status-summary">
            {statusSummary.length ? statusSummary.map((item) => (
              <em className={`timesheet-status-summary-pill is-${item.status.toLowerCase()}`} key={item.status}>
                <span>{statusLabel(item.status)}</span>
                <b>{formatHours(item.hours)}</b>
                <small>{item.count}</small>
              </em>
            )) : (
              <em className="timesheet-status-summary-pill is-empty">
                <span>No status records</span>
              </em>
            )}
          </div>
        </article>
        <label>
          Range start
          <input type="date" value={rangeStart} onChange={(event) => setRangeStart(event.target.value)} />
        </label>
        <label>
          Range end
          <input type="date" value={rangeEnd} onChange={(event) => setRangeEnd(event.target.value)} />
        </label>
      </section>

      <section className={`timesheet-filter-bar${isIndividualTimesheet ? " is-personal" : ""}`}>
        {!isIndividualTimesheet ? (
          <label>
            Staff
            <Select
              showSearch
              value={staffFilter}
              optionFilterProp="searchText"
              onChange={(value) => setStaffFilter(value || "ALL")}
              options={[
                { value: "ALL", searchText: "All staff", label: "All staff" },
                ...staffChoices.map((staff) => ({
                  value: staff.id,
                  searchText: `${staff.name} ${staff.email}`,
                  label: (
                    <span className="timesheet-staff-option">
                      <strong>{staff.name}</strong>
                      <small>{staff.email || "No email"}</small>
                    </span>
                  ),
                })),
              ]}
            />
          </label>
        ) : null}
        <label>
          Pay code
          <select value={payCodeFilter} onChange={(event) => setPayCodeFilter(event.target.value)}>
            <option value="ALL">All pay codes</option>
            {PAY_CODE_OPTIONS.map((payCode) => (
              <option value={payCode} key={payCode}>{titleCase(payCode)}</option>
            ))}
          </select>
        </label>
        <label>
          Status
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="ALL">All statuses</option>
            {visibleStatusOptions.map((status) => (
              <option value={status} key={status}>{titleCase(status)}</option>
            ))}
          </select>
        </label>
      </section>

      <section className="timesheet-print-area" aria-hidden="true">
        <header>
          <div>
            <h1>{pageTitle}</h1>
            <p>{pageSubtitle}</p>
          </div>
          <aside>
            <span>Printed</span>
            <strong>{printedAtLabel}</strong>
          </aside>
        </header>
        <div className="timesheet-print-meta">
          <span>Range: <strong>{rangeStart || "Any"} - {rangeEnd || "Any"}</strong></span>
          <span>Staff: <strong>{staffFilter === "ALL" ? "All staff" : staffChoices.find((staff) => staff.id === staffFilter)?.name || "Selected staff"}</strong></span>
          <span>Status: <strong>{statusFilter === "ALL" ? "All statuses" : titleCase(statusFilter)}</strong></span>
          <span>Total: <strong>{formatHours(rangeTotal)}</strong></span>
        </div>
        <table>
          <thead>
            <tr>
              <th>Staff</th>
              <th>Start</th>
              <th>End</th>
              <th>Hours</th>
              <th>Pay code</th>
              <th>Status</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {filteredTimesheetEntries.length ? filteredTimesheetEntries.map(({ row, entry, entryIndex, start, end, payCode }) => (
              <tr key={`print-${row.id || row.workDate || row.date || "timesheet"}-${entry.id || entryIndex}`}>
                <td>
                  <strong>{staffNameWithProfiles(row, profilesById, "Care staff")}</strong>
                  <small>{staffEmailWithProfiles(row, profilesById) || row.caregiverUserId || ""}</small>
                </td>
                <td>{formatTimestamp(start)}</td>
                <td>{formatTimestamp(end)}</td>
                <td>{formatHours(entry.hours ?? hoursBetween(start, end))}</td>
                <td>{titleCase(payCode)}</td>
                <td>{statusLabel(statusFor(row))}</td>
                <td>{entry.notes || row.notes || ""}</td>
              </tr>
            )) : (
              <tr>
                <td colSpan={7}>No timesheet records found for this range.</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <div className={`timesheet-page-grid${isStaffTimesheet ? "" : " is-list-only"}`}>
        {isStaffTimesheet && canCreateOnStaffPage ? (
        <Panel title="Add Timesheet">
          <div className="timesheet-form">
            <div className="timesheet-entry-list">
              <div className="timesheet-entry-list__head">
                <strong>Time entries</strong>
                <button className="mini-btn" type="button" onClick={addEntry}>
                  <i className="ti ti-plus" /> Add time
                </button>
              </div>
              {form.entries.map((entry, index) => (
                <div className={`timesheet-entry-row${formConflictIndexes.has(index) ? " has-conflict" : ""}`} key={`${index}-${entry.startTimestamp}-${entry.endTimestamp}`}>
                  <label>
                    Start timestamp
                    <input type="datetime-local" value={entry.startTimestamp || ""} onChange={(event) => updateEntry(index, "startTimestamp", event.target.value)} />
                  </label>
                  <label>
                    End timestamp
                    <input type="datetime-local" value={entry.endTimestamp || ""} onChange={(event) => updateEntry(index, "endTimestamp", event.target.value)} />
                  </label>
                  <label>
                    Pay code (Shift Type)
                    <select value={entry.shiftType || "REGULAR"} onChange={(event) => updateEntry(index, "shiftType", event.target.value)}>
                      <option value="REGULAR">Regular</option>
                      <option value="OVERTIME">Overtime</option>
                    </select>
                  </label>
                  <strong>{formatHours(hoursBetween(entry.startTimestamp, entry.endTimestamp))}</strong>
                  <button className="mini-btn danger" type="button" onClick={() => removeEntry(index)} disabled={form.entries.length === 1}>
                    <i className="ti ti-trash" />
                  </button>
                  <label className="timesheet-entry-notes">
                    Notes
                    <textarea value={entry.notes || ""} onChange={(event) => updateEntry(index, "notes", event.target.value)} />
                  </label>
                </div>
              ))}
              <ConflictNotice conflicts={formConflicts} />
            </div>

            <div className="timesheet-form-footer">
              <span>Selected day total: <strong>{formatHours(formHours)}</strong></span>
              <button className="btn primary" type="button" onClick={saveTimesheet} disabled={saving || formConflicts.length}>
                <i className="ti ti-device-floppy" /> {saving ? "Saving..." : "Save timesheet"}
              </button>
            </div>
          </div>
        </Panel>
        ) : null}

        <Panel title="Previous Timesheets">
          <div className="timesheet-table">
            <div className="timesheet-table-row timesheet-table-head">
              <span>Staff</span>
              <span>Start</span>
              <span>End</span>
              <span>Hours</span>
              <span>Pay code</span>
              <span>Status</span>
              <span>Actions</span>
            </div>
            {paginatedTimesheetEntries.length ? paginatedTimesheetEntries.map(({ row, entry, entryIndex, start, end, payCode }) => {
              const staff = staffNameWithProfiles(row, profilesById, "Care staff");
              const entryHours = entry.hours ?? hoursBetween(start, end);
              const canMutateRow = canMutateTimesheetRow(row);
              const canReviewRow = canReviewTimesheetRow(row);
              return (
                <div className="timesheet-table-row" key={`${row.id || row.workDate || row.date || "timesheet"}-${entry.id || entryIndex}`}>
                  <span className="timesheet-staff-cell">
                    <strong>{staff}</strong>
                    <small>{staffEmailWithProfiles(row, profilesById) || row.caregiverUserId || ""}</small>
                  </span>
                  <span>{formatTimestamp(start)}</span>
                  <span>{formatTimestamp(end)}</span>
                  <b>{formatHours(entryHours)}</b>
                  <PayCodePills codes={[payCode]} />
                  <span><em className={`timesheet-status is-${statusFor(row).toLowerCase()}`}>{titleCase(statusFor(row))}</em></span>
                  <span className="timesheet-row-actions">
                    <button
                      className="icon-button timesheet-detail-button"
                      type="button"
                      title="View timesheet details"
                      aria-label="View timesheet details"
                      onClick={() => setSelectedTimesheet(row)}
                    >
                      <i className="ti ti-eye" />
                    </button>
                    {canMutateRow ? (
                      <button
                        className="icon-button timesheet-edit-button"
                        type="button"
                        title="Edit timesheet"
                        aria-label="Edit timesheet"
                        onClick={() => openEditTimesheet(row)}
                      >
                        <i className="ti ti-pencil" />
                      </button>
                    ) : null}
                    {canReviewRow ? (
                      <>
                        <button
                          className="icon-button timesheet-approve-button"
                          type="button"
                          title="Approve timesheet"
                          aria-label="Approve timesheet"
                          onClick={() => openReviewTimesheet(row, "APPROVED")}
                        >
                          <i className="ti ti-circle-check" />
                        </button>
                        <button
                          className="icon-button timesheet-decline-button"
                          type="button"
                          title="Decline timesheet"
                          aria-label="Decline timesheet"
                          onClick={() => openReviewTimesheet(row, "DECLINED")}
                        >
                          <i className="ti ti-circle-x" />
                        </button>
                      </>
                    ) : null}
                    {canMutateRow ? (
                      <button
                        className="icon-button timesheet-delete-button"
                        type="button"
                        title="Cancel timesheet"
                        aria-label="Cancel timesheet"
                        onClick={() => deleteTimesheet(row)}
                      >
                        <i className="ti ti-circle-x" />
                      </button>
                    ) : null}
                  </span>
                </div>
              );
            }) : (
              <div className="timesheet-empty">
                <i className="ti ti-calendar-time" />
                <span>No timesheets found for this range.</span>
              </div>
            )}
          </div>
          {filteredTimesheetEntries.length ? (
            <div className="timesheet-pagination">
              <span>
                Showing <strong>{paginationStart}-{paginationEnd}</strong> of <strong>{filteredTimesheetEntries.length}</strong>
              </span>
              <label>
                Rows
                <select
                  value={pageSize}
                  onChange={(event) => setPageSize(Number(event.target.value))}
                >
                  {TIMESHEET_PAGE_SIZE_OPTIONS.map((size) => (
                    <option value={size} key={size}>{size}</option>
                  ))}
                </select>
              </label>
              <div className="timesheet-pagination-actions">
                <button
                  className="mini-btn"
                  type="button"
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  disabled={currentPage <= 1}
                >
                  <i className="ti ti-chevron-left" /> Previous
                </button>
                <strong>Page {currentPage} of {totalPages}</strong>
                <button
                  className="mini-btn"
                  type="button"
                  onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                  disabled={currentPage >= totalPages}
                >
                  Next <i className="ti ti-chevron-right" />
                </button>
              </div>
            </div>
          ) : null}
        </Panel>
      </div>
      <Drawer
        title="Timesheet Details"
        placement="right"
        width={680}
        open={Boolean(selectedTimesheet)}
        onClose={() => setSelectedTimesheet(null)}
      >
        {selectedTimesheet ? (
          <div className="timesheet-details-drawer">
            <section>
              <span>Care staff</span>
              <strong>{staffNameFor(selectedTimesheet, staffName)}</strong>
              <small>{staffEmailWithProfiles(selectedTimesheet, profilesById) || selectedTimesheet.caregiverUserId || ""}</small>
            </section>
            <div className="timesheet-detail-grid">
              <span>Date</span>
              <strong>{selectedTimesheet.workDate || selectedTimesheet.date || "N/A"}</strong>
              <span>Total hours</span>
              <strong>{formatHours(selectedTimesheet.totalHours ?? totalHours(rowEntries(selectedTimesheet)))}</strong>
              <span>Pay code</span>
              <strong><PayCodePills codes={payCodesFor(selectedTimesheet)} /></strong>
              <span>Status</span>
              <strong>{titleCase(statusFor(selectedTimesheet))}</strong>
              <span>Review reason</span>
              <strong>{selectedTimesheet.statusReason || selectedTimesheet.reason || "Not provided"}</strong>
            </div>
            <section>
              <span>Time entries</span>
              <div className="timesheet-detail-entries">
                {rowEntries(selectedTimesheet).map((entry, index) => (
                  <article key={entry.id || `${entryStart(entry)}-${entryEnd(entry)}-${index}`}>
                    <strong>{formatTimestamp(entryStart(entry))} - {formatTimestamp(entryEnd(entry))}</strong>
                    <PayCodePills codes={[entry.payCode || entry.shiftType || "REGULAR"]} />
                    <b>{formatHours(entry.hours ?? hoursBetween(entryStart(entry), entryEnd(entry)))}</b>
                  </article>
                ))}
              </div>
            </section>
            <section>
              <span>Notes</span>
              <p>{selectedTimesheet.notes || "No notes provided."}</p>
            </section>
          </div>
        ) : null}
      </Drawer>
      <Drawer
        title={isMyTimesheet ? "Add Timesheet" : "New Timesheet"}
        placement="right"
        width={760}
        open={newDrawerOpen}
        onClose={() => setNewDrawerOpen(false)}
      >
        <div className="timesheet-form timesheet-edit-drawer">
          {!isMyTimesheet && canCreateForAnyStaff ? (
          <label className="resident-form-field timesheet-staff-search-field">
            Staff
            <Select
              showSearch
              allowClear
              value={selectedNewCareProfileId || undefined}
              placeholder="Search staff by name or email"
              optionFilterProp="searchText"
              onChange={(value) => setSelectedNewCareProfileId(value || "")}
              options={staffChoices.map((staff) => ({
                value: staff.id,
                searchText: `${staff.name} ${staff.email}`,
                label: (
                  <span className="timesheet-staff-option">
                    <strong>{staff.name}</strong>
                    <small>{staff.email || "No email"}</small>
                  </span>
                ),
              }))}
            />
          </label>
          ) : (
            <section className="timesheet-edit-staff">
              <span>Care staff</span>
              <strong>{activeFormStaff?.name || staffName}</strong>
              <small>{activeFormStaff?.email || staffChoices[0]?.email || ""}</small>
            </section>
          )}
          <div className="timesheet-entry-list">
            <div className="timesheet-entry-list__head">
              <strong>Time entries</strong>
              <button className="mini-btn" type="button" onClick={addEntry}>
                <i className="ti ti-plus" /> Add time
              </button>
            </div>
            {form.entries.map((entry, index) => (
              <div className={`timesheet-entry-row compact${formConflictIndexes.has(index) ? " has-conflict" : ""}`} key={`${index}-${entry.startTimestamp}-${entry.endTimestamp}`}>
                <label>
                  Start timestamp
                  <input type="datetime-local" value={entry.startTimestamp || ""} onChange={(event) => updateEntry(index, "startTimestamp", event.target.value)} />
                </label>
                <label>
                  End timestamp
                  <input type="datetime-local" value={entry.endTimestamp || ""} onChange={(event) => updateEntry(index, "endTimestamp", event.target.value)} />
                </label>
                <label>
                  Pay code
                  <select value={entry.shiftType || "REGULAR"} onChange={(event) => updateEntry(index, "shiftType", event.target.value)}>
                    {PAY_CODE_OPTIONS.map((payCode) => (
                      <option value={payCode} key={payCode}>{titleCase(payCode)}</option>
                    ))}
                  </select>
                </label>
                <strong>{formatHours(hoursBetween(entry.startTimestamp, entry.endTimestamp))}</strong>
                <button className="mini-btn danger" type="button" onClick={() => removeEntry(index)} disabled={form.entries.length === 1}>
                  <i className="ti ti-trash" />
                </button>
                <label className="timesheet-entry-notes">
                  Notes
                  <textarea value={entry.notes || ""} onChange={(event) => updateEntry(index, "notes", event.target.value)} />
                </label>
              </div>
            ))}
            <ConflictNotice conflicts={formConflicts} />
          </div>
          <div className="timesheet-form-footer">
            <span>Selected day total: <strong>{formatHours(formHours)}</strong></span>
            <button className="btn primary" type="button" onClick={saveTimesheet} disabled={saving || formConflicts.length}>
              <i className="ti ti-device-floppy" /> {saving ? "Saving..." : "Save timesheet"}
            </button>
          </div>
        </div>
      </Drawer>
      <Drawer
        title="Review Timesheet"
        placement="right"
        width={680}
        open={Boolean(reviewingTimesheet)}
        onClose={closeReviewTimesheet}
      >
        {reviewingTimesheet ? (
          <div className="timesheet-form timesheet-edit-drawer">
            <section className="timesheet-edit-staff">
              <span>Care staff</span>
              <strong>{staffNameWithProfiles(reviewingTimesheet, profilesById, "Care staff")}</strong>
              <small>{staffEmailWithProfiles(reviewingTimesheet, profilesById) || reviewingTimesheet.caregiverUserId || ""}</small>
            </section>
            <div className="timesheet-detail-grid">
              <span>Start</span>
              <strong>{formatTimestamp(entryStart(rowEntries(reviewingTimesheet)[0]))}</strong>
              <span>End</span>
              <strong>{formatTimestamp(entryEnd(rowEntries(reviewingTimesheet)[rowEntries(reviewingTimesheet).length - 1]))}</strong>
              <span>Total hours</span>
              <strong>{formatHours(reviewingTimesheet.totalHours ?? totalHours(rowEntries(reviewingTimesheet)))}</strong>
              <span>Current status</span>
              <strong>{titleCase(statusFor(reviewingTimesheet))}</strong>
            </div>
            <section className="timesheet-review-clockinout">
              <div className="timesheet-review-clockinout-head">
                <div>
                  <span>ClockInOut comparison</span>
                  <strong>
                    {reviewingTimesheetPeriod?.startDate === reviewingTimesheetPeriod?.endDate
                      ? reviewingTimesheetPeriod?.startDate
                      : `${reviewingTimesheetPeriod?.startDate} - ${reviewingTimesheetPeriod?.endDate}`}
                  </strong>
                </div>
                <button className="mini-btn timesheet-nav-blue" type="button" onClick={loadReviewClockInOutDetails} disabled={reviewAttendanceLoading}>
                  <i className="ti ti-clock-search" /> {reviewAttendanceLoading ? "Loading..." : reviewAttendanceOpen ? "Refresh ClockInOut" : "Show ClockInOut details"}
                </button>
              </div>
              {reviewAttendanceOpen ? (
                <div className="timesheet-review-clockinout-list">
                  {reviewAttendanceError ? <p className="form-error">{reviewAttendanceError}</p> : null}
                  {!reviewAttendanceError && reviewAttendanceLoading ? (
                    <p>Loading ClockInOut details...</p>
                  ) : null}
                  {!reviewAttendanceError && !reviewAttendanceLoading && reviewAttendanceRecords.length ? (
                    reviewAttendanceRecords.map((record, index) => (
                      <article key={record.id || `${clockInTimestampFor(record)}-${index}`}>
                        <div>
                          <span>Clock in</span>
                          <strong>{formatTimestamp(clockInTimestampFor(record))}</strong>
                        </div>
                        <div>
                          <span>Clock out</span>
                          <strong>{formatTimestamp(clockOutTimestampFor(record))}</strong>
                        </div>
                        <div>
                          <span>Hours</span>
                          <strong>{formatHours(record.hours ?? hoursBetween(clockInTimestampFor(record), clockOutTimestampFor(record)))}</strong>
                        </div>
                        <div>
                          <span>Status</span>
                          <strong>{recordStatusLabel(record.status)}</strong>
                        </div>
                        <div className="timesheet-review-clockinout-wide">
                          <span>Location</span>
                          <strong>{locationText(record.clockOutLocation || record.clockInLocation)}</strong>
                        </div>
                        <div className="timesheet-review-clockinout-wide">
                          <span>Notes</span>
                          <strong>{[record.clockInNotes, record.clockOutNotes].map(clean).filter(Boolean).join(" / ") || "No notes"}</strong>
                        </div>
                      </article>
                    ))
                  ) : null}
                  {!reviewAttendanceError && !reviewAttendanceLoading && !reviewAttendanceRecords.length ? (
                    <div className="timesheet-empty">
                      <i className="ti ti-clock" />
                      <span>No ClockInOut records found for this day or period.</span>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </section>
            <label className="resident-form-field">
              Review decision
              <select value={reviewForm.status} onChange={(event) => setReviewForm((current) => ({ ...current, status: event.target.value }))}>
                {currentReviewStatusOptions.map((status) => (
                  <option value={status} key={status}>
                    {titleCase(status)}
                  </option>
                ))}
              </select>
            </label>
            <label className="resident-form-field full-span">
              Reason
              <textarea
                value={reviewForm.reason}
                onChange={(event) => setReviewForm((current) => ({ ...current, reason: event.target.value }))}
                placeholder="Add a reason for this review decision"
              />
            </label>
            <div className="timesheet-form-footer">
              <span>Decision: <strong>{titleCase(reviewForm.status)}</strong></span>
              <button className="btn primary" type="button" onClick={submitReview} disabled={saving || (ADMIN_REVIEW_STATUSES.includes(reviewForm.status) && (!canApproveTimesheets || reviewingOwnTimesheet))}>
                <i className="ti ti-check" /> {saving ? "Saving..." : "Save review"}
              </button>
            </div>
          </div>
        ) : null}
      </Drawer>
      <Drawer
        title="Edit Timesheet"
        placement="right"
        width={760}
        open={Boolean(editingTimesheet)}
        onClose={() => setEditingTimesheet(null)}
      >
        {editingTimesheet ? (
          <div className="timesheet-form timesheet-edit-drawer">
            <section className="timesheet-edit-staff timesheet-edit-staff-with-actions">
              <div>
                <span>Care staff</span>
                <strong>{staffNameFor(editingTimesheet, staffName)}</strong>
                <small>{staffEmailWithProfiles(editingTimesheet, profilesById) || editingTimesheet.caregiverUserId || ""}</small>
              </div>
              <span className="timesheet-row-actions">
                <button
                  className="icon-button timesheet-detail-button"
                  type="button"
                  title="View timesheet details"
                  aria-label="View timesheet details"
                  onClick={() => {
                    const row = editingTimesheet;
                    setEditingTimesheet(null);
                    setSelectedTimesheet(row);
                  }}
                >
                  <i className="ti ti-eye" />
                </button>
                {canReviewTimesheetRow(editingTimesheet) ? (
                  <>
                    <button
                      className="icon-button timesheet-approve-button"
                      type="button"
                      title="Approve timesheet"
                      aria-label="Approve timesheet"
                      onClick={() => {
                        const row = editingTimesheet;
                        setEditingTimesheet(null);
                        openReviewTimesheet(row, "APPROVED");
                      }}
                    >
                      <i className="ti ti-circle-check" />
                    </button>
                    <button
                      className="icon-button timesheet-decline-button"
                      type="button"
                      title="Decline timesheet"
                      aria-label="Decline timesheet"
                      onClick={() => {
                        const row = editingTimesheet;
                        setEditingTimesheet(null);
                        openReviewTimesheet(row, "DECLINED");
                      }}
                    >
                      <i className="ti ti-circle-x" />
                    </button>
                  </>
                ) : null}
                <button
                  className="icon-button timesheet-delete-button"
                  type="button"
                  title="Cancel timesheet"
                  aria-label="Cancel timesheet"
                  onClick={() => {
                    const row = editingTimesheet;
                    setEditingTimesheet(null);
                    deleteTimesheet(row);
                  }}
                >
                  <i className="ti ti-circle-x" />
                </button>
              </span>
            </section>
            {canEditTimesheetManagerOptions ? (
              <div className="resident-form-grid compact">
                <label className="resident-form-field">
                  Status
                  <select value={editForm.status} onChange={(event) => setEditForm((current) => ({ ...current, status: event.target.value }))}>
                    {editableStatusOptions.map((status) => (
                      <option value={status} key={status}>{titleCase(status)}</option>
                    ))}
                  </select>
                </label>
              </div>
            ) : null}
            <div className="timesheet-entry-list">
              <div className="timesheet-entry-list__head">
                <strong>Time entries</strong>
                {canEditTimesheetManagerOptions ? (
                  <button className="mini-btn" type="button" onClick={addEditEntry}>
                    <i className="ti ti-plus" /> Add time
                  </button>
                ) : null}
              </div>
              {editForm.entries.map((entry, index) => (
                <div className={`timesheet-entry-row compact${canEditTimesheetManagerOptions ? "" : " is-simple-edit"}${editConflictIndexes.has(index) ? " has-conflict" : ""}`} key={`${index}-${entry.startTimestamp}-${entry.endTimestamp}`}>
                  <label>
                    Start timestamp
                    <input type="datetime-local" value={entry.startTimestamp || ""} onChange={(event) => updateEditEntry(index, "startTimestamp", event.target.value)} />
                  </label>
                  <label>
                    End timestamp
                    <input type="datetime-local" value={entry.endTimestamp || ""} onChange={(event) => updateEditEntry(index, "endTimestamp", event.target.value)} />
                  </label>
                  {canEditTimesheetManagerOptions ? (
                    <label>
                      Pay code
                      <select value={entry.shiftType || "REGULAR"} onChange={(event) => updateEditEntry(index, "shiftType", event.target.value)}>
                        {PAY_CODE_OPTIONS.map((payCode) => (
                          <option value={payCode} key={payCode}>{titleCase(payCode)}</option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                  <strong>{formatHours(hoursBetween(entry.startTimestamp, entry.endTimestamp))}</strong>
                  {canEditTimesheetManagerOptions ? (
                    <button className="mini-btn danger" type="button" onClick={() => removeEditEntry(index)} disabled={editForm.entries.length === 1}>
                      <i className="ti ti-trash" />
                    </button>
                  ) : null}
                  <label className="timesheet-entry-notes">
                    Notes
                    <textarea value={entry.notes || ""} onChange={(event) => updateEditEntry(index, "notes", event.target.value)} />
                  </label>
                </div>
              ))}
              <ConflictNotice conflicts={editConflicts} />
            </div>
            <div className="timesheet-form-footer">
              <span>Selected day total: <strong>{formatHours(totalHours(editForm.entries))}</strong></span>
              <button className="btn primary" type="button" onClick={saveEditedTimesheet} disabled={saving || editConflicts.length}>
                <i className="ti ti-device-floppy" /> {saving ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>
        ) : null}
      </Drawer>
    </CareShell>
  );
}
